/**
 * Live-subscribed data hooks for the Credit Card Submission tool.
 * Same `{ status, ... }` shape as the invoice hooks.
 */

import { useEffect, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import type { CreditCardStatement, CreditCardLineItem } from '@daaily/shared';

const COLLECTION = 'creditCardStatements';

type Status = 'loading' | 'ready' | 'error';

export interface ListResult {
  status: Status;
  statements: CreditCardStatement[];
  error?: string;
}

export interface DocResult {
  status: Status;
  statement: CreditCardStatement | null;
  error?: string;
}

export interface LineItemsResult {
  status: Status;
  lineItems: CreditCardLineItem[];
  error?: string;
}

/** Live-subscribe to the caller's own statements, newest first. */
export function useMyCreditCardStatements(email: string | null): ListResult {
  const [state, setState] = useState<ListResult>({
    status: 'loading',
    statements: [],
  });

  useEffect(() => {
    if (!email) {
      setState({ status: 'loading', statements: [] });
      return;
    }
    const q = query(
      collection(db, COLLECTION),
      where('ownerEmail', '==', email.toLowerCase()),
      orderBy('createdAt', 'desc'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const statements: CreditCardStatement[] = [];
        snap.forEach((d) =>
          statements.push({ ...(d.data() as CreditCardStatement), id: d.id }),
        );
        setState({ status: 'ready', statements });
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error('[useMyCreditCardStatements] snapshot error:', err);
        setState({ status: 'error', statements: [], error: err.message });
      },
    );
    return unsub;
  }, [email]);

  return state;
}

/** Filter values for the approvals page. */
export type CreditCardApprovalFilter =
  | 'submitted'
  | 'approved'
  | 'rejected'
  | 'all'
  | 'assigned';

/**
 * Live-subscribe to statements by approval filter (approver queue).
 *   submitted → FIFO (oldest submittedAt first); approved/rejected/all → newest first.
 *   assigned  → every statement that was assigned to someone (any status), newest
 *               first; filtered client-side (assignedAt set) so no composite index.
 */
export function useCreditCardStatementsByStatus(filter: CreditCardApprovalFilter): ListResult {
  const [state, setState] = useState<ListResult>({ status: 'loading', statements: [] });

  useEffect(() => {
    const base = collection(db, COLLECTION);
    const q =
      filter === 'submitted'
        ? query(base, where('status', '==', 'submitted'), orderBy('submittedAt', 'asc'))
        : filter === 'all' || filter === 'assigned'
          ? query(base, orderBy('createdAt', 'desc'))
          : query(base, where('status', '==', filter), orderBy('createdAt', 'desc'));

    const unsub = onSnapshot(
      q,
      (snap) => {
        let statements: CreditCardStatement[] = [];
        snap.forEach((d) =>
          statements.push({ ...(d.data() as CreditCardStatement), id: d.id }),
        );
        if (filter === 'assigned') statements = statements.filter((s) => s.assignedAt != null);
        setState({ status: 'ready', statements });
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error('[useCreditCardStatementsByStatus] snapshot error:', err);
        setState({ status: 'error', statements: [], error: err.message });
      },
    );
    return unsub;
  }, [filter]);

  return state;
}

/** Live-subscribe to a single statement doc by id. */
export function useCreditCardStatement(id: string | null): DocResult {
  const [state, setState] = useState<DocResult>({
    status: 'loading',
    statement: null,
  });

  useEffect(() => {
    if (!id) {
      setState({ status: 'loading', statement: null });
      return;
    }
    const unsub = onSnapshot(
      doc(db, COLLECTION, id),
      (snap) => {
        setState({
          status: 'ready',
          statement: snap.exists()
            ? { ...(snap.data() as CreditCardStatement), id: snap.id }
            : null,
        });
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error('[useCreditCardStatement] snapshot error:', err);
        setState({ status: 'error', statement: null, error: err.message });
      },
    );
    return unsub;
  }, [id]);

  return state;
}

/** Live-subscribe to a statement's line items, ordered by lineNumber. */
export function useCreditCardLineItems(id: string | null): LineItemsResult {
  const [state, setState] = useState<LineItemsResult>({
    status: 'loading',
    lineItems: [],
  });

  useEffect(() => {
    if (!id) {
      setState({ status: 'loading', lineItems: [] });
      return;
    }
    const q = query(
      collection(db, COLLECTION, id, 'lineItems'),
      orderBy('lineNumber', 'asc'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const lineItems: CreditCardLineItem[] = [];
        snap.forEach((d) =>
          lineItems.push({ ...(d.data() as CreditCardLineItem), id: d.id }),
        );
        setState({ status: 'ready', lineItems });
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error('[useCreditCardLineItems] snapshot error:', err);
        setState({ status: 'error', lineItems: [], error: err.message });
      },
    );
    return unsub;
  }, [id]);

  return state;
}
